import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { getAdmins, inviteAdmin, updateAdmin, deleteAdmin } from "../services/adminService";
import { getUser } from "../utils/helper";
import useConfirm from "../hooks/useConfirm";
import ConfirmModal from "../components/ui/ConfirmModal";
import { SkeletonRows } from "../components/ui/Skeleton";
import "../assets/css/pages/roles.css";

const ROLES = ["Owner", "Admin", "Manager", "Viewer"];
const ROLE_META = {
  Owner: { color: "#665DA9", desc: "Unrestricted. Manages billing, roles and the workspace." },
  Admin: { color: "#09A7DF", desc: "Manage members, hours, history and exports." },
  Manager: { color: "#34D3A6", desc: "View and edit attendance for an assigned team." },
  Viewer: { color: "#828F9C", desc: "Read-only access to reports and history." },
};
// Permission matrix — capability by role [Owner, Admin, Manager, Viewer].
const PERMS = [
  ["View attendance", [1, 1, 1, 1]],
  ["Edit check-in / out", [1, 1, 1, 0]],
  ["Manage members", [1, 1, 0, 0]],
  ["Export reports", [1, 1, 1, 1]],
  ["Manage roles & admins", [1, 0, 0, 0]],
  ["Workspace settings", [1, 0, 0, 0]],
  ["Billing", [1, 0, 0, 0]],
];
const initials = (name) => (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
const roleColor = (r) => (ROLE_META[r] || ROLE_META.Admin).color;

const Roles = () => {
  const me = getUser() || {};
  const { confirm, modalProps } = useConfirm();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("Admin");
  const [inviteResult, setInviteResult] = useState(null); // { link, email } when Slack DM couldn't be delivered
  const [openMenu, setOpenMenu] = useState(null);

  const load = async () => {
    setLoading(true);
    setAdmins(await getAdmins());
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = {};
    ROLES.forEach((r) => (c[r] = 0));
    admins.forEach((a) => { c[a.role] = (c[a.role] || 0) + 1; });
    return c;
  }, [admins]);

  const submitInvite = async (e) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) { toast.error("Enter an email"); return; }
    const { status, admin, message } = await inviteAdmin({ email, role: inviteRole });
    if (status === 200) {
      toast.success(message || `Invite sent to ${email}`);
      setInviteEmail(""); setInviteRole("Admin");
      if (admin && admin.delivered === false && admin.inviteLink) {
        // Couldn't DM on Slack — keep the panel open and surface the link to copy.
        setInviteResult({ link: admin.inviteLink, email: admin.email });
      } else {
        setInviteResult(null); setInviteOpen(false);
      }
      load();
    } else {
      toast.error(message || "Couldn't send invite");
    }
  };

  const copyInviteLink = async () => {
    if (!inviteResult?.link) return;
    try { await navigator.clipboard.writeText(inviteResult.link); toast.success("Link copied"); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };

  const toggleInvite = () => {
    setInviteResult(null);
    setInviteOpen((v) => !v);
  };

  const changeRole = async (a, role) => {
    setOpenMenu(null);
    if (a.role === role) return;
    setAdmins((list) => list.map((x) => (x.id === a.id ? { ...x, role } : x)));
    const { status } = await updateAdmin(a.id, { role });
    if (status !== 200) { toast.error("Couldn't update role"); load(); }
    else toast.success(`${a.name} is now ${role}`);
  };

  const revoke = async (a) => {
    setOpenMenu(null);
    const ok = await confirm({ title: `Revoke ${a.name}?`, message: "They will lose admin access to the dashboard." });
    if (!ok) return;
    setAdmins((list) => list.filter((x) => x.id !== a.id));
    const { status } = await deleteAdmin(a.id);
    if (status !== 200) { toast.error("Couldn't revoke admin"); load(); }
    else toast.success(`${a.name} removed`);
  };

  return (
    <>
      {/* Role summary cards */}
      <div className="roles-summary">
        {ROLES.map((r) => (
          <div className="role-card" key={r} style={{ "--rc": roleColor(r) }}>
            <div className="top">
              <span className="role-name">{r}</span>
              <span className="role-count" style={{ color: roleColor(r), background: `${roleColor(r)}18` }}>
                {loading ? "…" : counts[r]}
              </span>
            </div>
            <div className="role-desc">{ROLE_META[r].desc}</div>
          </div>
        ))}
      </div>

      <div className="roles-grid">
        {/* Admins list */}
        <div className="admins-card">
          <div className="admins-head">
            <div>
              <div className="t">Admins &amp; managers</div>
              <div className="s">People who can administer attendance</div>
            </div>
            <button type="button" className="invite-btn" onClick={toggleInvite}>
              <svg><use href="#i-plus" /></svg>Invite admin
            </button>
          </div>

          {inviteOpen && (
            <>
              <form className="invite-form" onSubmit={submitInvite}>
                <input type="email" placeholder="name@startise.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} autoFocus />
                <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                  {ROLES.filter((r) => r !== "Owner").map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <button type="submit" className="invite-send">Send invite</button>
              </form>
              {inviteResult && (
                <div className="invite-link-fallback">
                  <div className="ilf-note">Couldn't reach {inviteResult.email} on Slack. Share this set-password link:</div>
                  <div className="ilf-row">
                    <input readOnly value={inviteResult.link} onFocus={(e) => e.target.select()} />
                    <button type="button" className="invite-send" onClick={copyInviteLink}>Copy</button>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="admins-grid admins-colhead">
            <div>Person</div><div>Role</div><div>Scope</div><div></div>
          </div>

          {loading && <SkeletonRows count={4} />}
          {!loading && admins.length === 0 && <div className="admin-empty">No admins yet.</div>}
          {!loading && admins.map((a) => (
            <div className="admins-grid admins-row" key={a.id}>
              <div className="admin-person">
                {a.avatar ? <img className="av av-36" src={a.avatar} alt="" style={{ objectFit: "cover" }} /> : <div className="av av-36 av-MH">{initials(a.name)}</div>}
                <div style={{ minWidth: 0 }}>
                  <div className="admin-name">
                    {a.name}{a.id === me.id ? " (you)" : ""}
                    {a.status === "pending" && <span className="admin-pending" title="Invited — hasn't set a password yet">Pending</span>}
                  </div>
                  <div className="admin-email">{a.email}</div>
                </div>
              </div>
              <div><span className="role-badge" style={{ color: roleColor(a.role), background: `${roleColor(a.role)}16` }}>{a.role}</span></div>
              <div className="admin-scope">{a.scope || "—"}</div>
              <div style={{ position: "relative", display: "flex", justifyContent: "flex-end" }}>
                <button className="admin-menu-btn" onClick={(e) => { e.stopPropagation(); setOpenMenu(openMenu === a.id ? null : a.id); }} aria-label="Admin actions">
                  <svg><use href="#i-dots" /></svg>
                </button>
                {openMenu === a.id && (
                  <div className="admin-menu" style={{ right: 0, top: 36 }} onClick={(e) => e.stopPropagation()}>
                    <div className="mlabel">Set role</div>
                    {ROLES.map((r) => (
                      <button key={r} className={a.role === r ? "on" : ""} onClick={() => changeRole(a, r)}>{r}{a.role === r ? " ✓" : ""}</button>
                    ))}
                    {a.id !== me.id && (
                      <>
                        <div className="sep" />
                        <button className="danger" onClick={() => revoke(a)}>Revoke access</button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Permission matrix */}
        <div className="perm-card">
          <div className="perm-head">
            <div className="t">Permission matrix</div>
            <div className="s">What each role can do</div>
          </div>
          {/* Header + rows scroll together on narrow screens. */}
          <div className="table-scroll">
          <div className="perm-grid perm-colhead">
            <div className="pl">Permission</div><div>Own</div><div>Adm</div><div>Mgr</div><div>View</div>
          </div>
          {PERMS.map(([name, cells]) => (
            <div className="perm-grid perm-row" key={name}>
              <div className="perm-name">{name}</div>
              {cells.map((on, i) => (
                <div className="perm-cell" key={i}>
                  {on ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                  ) : (
                    <span className="perm-dash" />
                  )}
                </div>
              ))}
            </div>
          ))}
          </div>
        </div>
      </div>

      {openMenu && <div onClick={() => setOpenMenu(null)} style={{ position: "fixed", inset: 0, zIndex: 15 }} />}
      <ConfirmModal {...modalProps} />
    </>
  );
};

export default Roles;
